import { CalendarDays, FolderKanban, QrCode } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

export type EntityType = 'project' | 'event' | 'station';

/**
 * Caso 16 — «¿dónde estoy?» de un vistazo.
 *
 * Las pantallas de proyecto, evento y estación comparten plantilla —migas,
 * fila de estado, título, pestañas— y por eso mismo se confunden entre sí:
 * lo único que cambia es un nombre propio que el usuario aún no conoce. Este
 * chip va junto al `h1` y dice el TIPO de cosa que se está mirando, con un
 * icono y un color fijos por tipo, de modo que la distinción no dependa de
 * leer nada.
 *
 * Los colores son los semánticos del tema (no hex) para que respondan a los
 * temas claro/oscuro igual que el resto de la página. La correspondencia
 * tipo→color es arbitraria pero ESTABLE: es una marca de identidad, no un
 * estado, así que no se reutiliza para nada más.
 */
const KINDS: Record<
  EntityType,
  { icon: typeof FolderKanban; labelKey: string; className: string }
> = {
  project: {
    icon: FolderKanban,
    labelKey: 'entityType.project',
    className:
      'bg-[var(--color-accent-15)] text-[var(--color-ink-accent)]',
  },
  event: {
    icon: CalendarDays,
    labelKey: 'entityType.event',
    className:
      'bg-[color-mix(in_srgb,var(--color-info)_12%,transparent)] text-[var(--color-info)]',
  },
  station: {
    icon: QrCode,
    labelKey: 'entityType.station',
    className:
      'bg-[color-mix(in_srgb,var(--color-success)_12%,transparent)] text-[var(--color-success)]',
  },
};

export function EntityTypeBadge({
  type,
  className,
}: {
  type: EntityType;
  className?: string;
}) {
  const t = useT();
  const kind = KINDS[type];
  const Icon = kind.icon;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm font-bold uppercase tracking-wide',
        kind.className,
        className,
      )}
      style={{ fontFamily: 'var(--font-display)' }}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {t(kind.labelKey)}
    </span>
  );
}
